import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Input } from './ui/input';
import { Pagination } from './ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  TrendingUp, 
  ArrowLeft, 
  Loader2, 
  RefreshCw,
  Building2,
  Search,
  X,
  Download,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';
import { supplierReportApiService, SupplierPerformanceReportDto } from '../services/supplierReportApi';
import { Badge } from './ui/badge';
import { CheckCircle } from 'lucide-react';
import { getPaginationConfig } from '../config/uiConfig';
import { useBranch } from '../contexts/BranchContext';

const SupplierPerformanceReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const [performanceData, setPerformanceData] = useState<SupplierPerformanceReportDto[]>([]);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Month and Year picker state - default to current month
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState<number>(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(currentDate.getMonth() + 1);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Pagination state
  const paginationConfig = getPaginationConfig();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(paginationConfig.defaultItemsPerPage);

  // Generate year options (last 5 years and next 1 year)
  const yearOptions = useMemo(() => {
    const currentYear = currentDate.getFullYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 1; i++) {
      years.push(i);
    }
    return years.reverse();
  }, []);

  // Month options
  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const loadPerformanceReport = async () => {
    if (!selectedBranch) {
      toast.error('Please select a branch to view the report');
      setPerformanceData([]);
      return;
    }

    try {
      setLoadingPerformance(true);
      const response = await supplierReportApiService.getSupplierPerformanceReport(
        selectedBranch,
        selectedYear,
        selectedMonth
      );
      
      if (response.success && response.data) {
        setPerformanceData(response.data);
      } else {
        setPerformanceData([]);
      }
    } catch (error) {
      // console.error('Error loading performance report:', error);
      setPerformanceData([]);
    } finally {
      setLoadingPerformance(false);
    }
  };

  useEffect(() => {
    if (selectedBranch) {
      loadPerformanceReport();
    }
  }, [selectedBranch, selectedYear, selectedMonth]);

  const formatCurrency = (value: number | undefined | null): string => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return `₹${value.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  // Helper functions for color coding
  const getOnTimeDeliveryColor = (percentage: number | null | undefined): string => {
    if (percentage === null || percentage === undefined) return 'text-gray-600';
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 80) return 'text-orange-600';
    return 'text-red-600';
  };

  const getDelayColor = (days: number | null | undefined): string => {
    if (days === null || days === undefined) return 'text-gray-600';
    if (days <= 1) return 'text-green-600';
    if (days <= 3) return 'text-orange-600';
    return 'text-red-600';
  };

  const getDefectRateColor = (rate: number | null | undefined): string => {
    if (rate === null || rate === undefined) return 'text-gray-600';
    if (rate <= 1) return 'text-green-600';
    if (rate <= 2) return 'text-orange-600';
    return 'text-red-600';
  };

  const getRating = (score: number | null | undefined): { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className: string } => {
    if (score === null || score === undefined) {
      return { label: 'N/A', variant: 'outline', className: 'bg-gray-100 text-gray-700' };
    }
    if (score >= 80) {
      return { label: 'Excellent', variant: 'default', className: 'bg-green-100 text-green-700' };
    }
    if (score >= 60) {
      return { label: 'Good', variant: 'secondary', className: 'bg-yellow-100 text-yellow-700' };
    }
    return { label: 'Needs Improvement', variant: 'destructive', className: 'bg-red-100 text-red-700' };
  };

  // Filter data based on search term
  const filteredData = useMemo(() => {
    let data = performanceData;
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      data = data.filter((supplier) =>
        supplier.supplierName?.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply sorting
    if (sortColumn) {
      data = [...data].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        
        switch (sortColumn) {
          case 'supplier':
            aValue = a.supplierName || '';
            bValue = b.supplierName || '';
            break;
          case 'onTimeDelivery':
            aValue = a.onTimeDeliveryPercentage ?? 0;
            bValue = b.onTimeDeliveryPercentage ?? 0;
            break;
          case 'avgDelay':
            aValue = a.averageDeliveryTime ?? 0;
            bValue = b.averageDeliveryTime ?? 0;
            break;
          case 'defectRate':
            aValue = a.rejectionRatePercentage ?? 0;
            bValue = b.rejectionRatePercentage ?? 0;
            break;
          case 'ordersCount':
            aValue = a.totalPoCount ?? 0;
            bValue = b.totalPoCount ?? 0;
            break;
          case 'totalValue':
            aValue = a.totalPoValue ?? 0;
            bValue = b.totalPoValue ?? 0;
            break;
          case 'deliveryRating':
            aValue = a.deliveryRating ?? 0;
            bValue = b.deliveryRating ?? 0;
            break;
          case 'rating':
            aValue = a.deliveryRating ?? 0;
            bValue = b.deliveryRating ?? 0;
            break;
          default:
            return 0;
        }
        
        // Handle string comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        // Handle number comparison
        return sortDirection === 'asc' 
          ? (aValue as number) - (bValue as number)
          : (bValue as number) - (aValue as number);
      });
    }
    
    return data;
  }, [performanceData, searchTerm, sortColumn, sortDirection]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  // Reset to first page when search term or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortColumn, sortDirection]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-primary-600" />
      : <ArrowDown className="h-4 w-4 text-primary-600" />;
  };

  const handleExport = () => {
    if (filteredData.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      // Define CSV headers
      const headers = [
        'Supplier',
        'On-time Delivery (%)',
        'Avg Delay (Days)',
        'Defect Rate (%)',
        'Orders Count',
        'Total Value',
        'Delivery Rating',
        'Rating'
      ];

      // Convert data to CSV rows
      const csvRows = [
        headers.join(','),
        ...filteredData.map((supplier) => {
          const onTimePercentage = supplier.onTimeDeliveryPercentage ?? 0;
          const avgDelay = supplier.averageDeliveryTime ?? 0;
          const defectRate = supplier.rejectionRatePercentage ?? 0;
          const rating = getRating(supplier.deliveryRating);
          
          const row = [
            `"${(supplier.supplierName || '').replace(/"/g, '""')}"`,
            onTimePercentage !== null && onTimePercentage !== undefined 
              ? `${onTimePercentage.toFixed(1)}%` 
              : 'N/A',
            avgDelay !== null && avgDelay !== undefined 
              ? avgDelay.toFixed(1) 
              : 'N/A',
            defectRate !== null && defectRate !== undefined 
              ? `${defectRate.toFixed(1)}%` 
              : 'N/A',
            supplier.totalPoCount ?? 'N/A',
            supplier.totalPoValue !== null && supplier.totalPoValue !== undefined 
              ? `₹${supplier.totalPoValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : 'N/A',
            supplier.deliveryRating !== null && supplier.deliveryRating !== undefined 
              ? supplier.deliveryRating.toFixed(2)
              : 'N/A',
            rating.label
          ];
          return row.join(',');
        })
      ];

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `Supplier_Performance_Report_${timestamp}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${filteredData.length} records to CSV`);
    } catch (error) {
      // console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        {/* Header */}
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Supplier Performance Report</CardTitle>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/reports')}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Reports
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadPerformanceReport}
                  disabled={loadingPerformance || !selectedBranch}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  {loadingPerformance ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={filteredData.length === 0}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Month and Year Picker */}
        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Select Month</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                <Select
                  value={selectedYear.toString()}
                  onValueChange={(value) => setSelectedYear(parseInt(value))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
                <Select
                  value={selectedMonth.toString()}
                  onValueChange={(value) => setSelectedMonth(parseInt(value))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((month) => (
                      <SelectItem key={month.value} value={month.value.toString()}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detailed Supplier Performance Table */}
        {!selectedBranch ? (
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600">Please select a branch to view the report</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : loadingPerformance ? (
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600 mr-3" />
                <span className="text-gray-600">Loading performance data...</span>
              </div>
            </CardContent>
          </Card>
        ) : performanceData.length === 0 ? (
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600">No data available for this report</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Try refreshing or check back later
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <CardTitle className="text-xl font-semibold text-gray-800">
                  Detailed Supplier Performance
                </CardTitle>
                {/* Search Input */}
                <div className="relative max-w-md w-full">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search by supplier name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-10"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Table Controls */}
              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {filteredData.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredData.length)} of {filteredData.length} {filteredData.length === 1 ? 'supplier' : 'suppliers'}
                  {performanceData.length !== filteredData.length && (
                    <span className="text-gray-400 ml-1">
                      (filtered from {performanceData.length} total)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Items per page:</span>
                  <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                    <SelectTrigger className="w-20 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paginationConfig.itemsPerPageOptions.map((option) => (
                        <SelectItem key={option} value={option.toString()}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {filteredData.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Search className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600">No suppliers match your search</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Try adjusting your search term
                    </p>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead 
                          className="font-semibold cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('supplier')}
                        >
                          <div className="flex items-center gap-2">
                            Supplier
                            {getSortIcon('supplier')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="font-semibold text-center cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('onTimeDelivery')}
                        >
                          <div className="flex items-center justify-center gap-2">
                            On-time Delivery
                            {getSortIcon('onTimeDelivery')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="font-semibold text-center cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('avgDelay')}
                        >
                          <div className="flex items-center justify-center gap-2">
                            Avg Delay (Days)
                            {getSortIcon('avgDelay')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="font-semibold text-center cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('defectRate')}
                        >
                          <div className="flex items-center justify-center gap-2">
                            Defect Rate
                            {getSortIcon('defectRate')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="font-semibold text-center cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('ordersCount')}
                        >
                          <div className="flex items-center justify-center gap-2">
                            Orders Count
                            {getSortIcon('ordersCount')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="font-semibold text-right cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('totalValue')}
                        >
                          <div className="flex items-center justify-end gap-2">
                            Total Value
                            {getSortIcon('totalValue')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="font-semibold text-center cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('deliveryRating')}
                        >
                          <div className="flex items-center justify-center gap-2">
                            Delivery Rating
                            {getSortIcon('deliveryRating')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="font-semibold text-center cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('rating')}
                        >
                          <div className="flex items-center justify-center gap-2">
                            Rating
                            {getSortIcon('rating')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedData.map((supplier, index) => {
                      const onTimePercentage = supplier.onTimeDeliveryPercentage ?? 0;
                      const avgDelay = supplier.averageDeliveryTime ?? 0;
                      const defectRate = supplier.rejectionRatePercentage ?? 0;
                      const rating = getRating(supplier.deliveryRating);
                      
                      return (
                        <TableRow key={supplier.supplierId || index} className="hover:bg-gray-50">
                          <TableCell className="font-medium">
                            <button
                              onClick={() => {
                                const params = new URLSearchParams();
                                params.append('supplierId', (supplier.supplierId || '').toString());
                                if (selectedBranch) params.append('branchId', selectedBranch.toString());
                                if (selectedYear) params.append('year', selectedYear.toString());
                                if (selectedMonth) params.append('month', selectedMonth.toString());
                                window.open(`/reports/supplier-performance/details?${params.toString()}`, '_blank');
                              }}
                              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            >
                              {supplier.supplierName || 'N/A'}
                            </button>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              {onTimePercentage >= 90 && (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              )}
                              <span className={getOnTimeDeliveryColor(onTimePercentage)}>
                                {onTimePercentage !== null && onTimePercentage !== undefined 
                                  ? `${onTimePercentage.toFixed(1)}%` 
                                  : 'N/A'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={getDelayColor(avgDelay)}>
                              {avgDelay !== null && avgDelay !== undefined 
                                ? avgDelay.toFixed(1) 
                                : 'N/A'}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={getDefectRateColor(defectRate)}>
                              {defectRate !== null && defectRate !== undefined 
                                ? `${defectRate.toFixed(1)}%` 
                                : 'N/A'}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            {supplier.totalPoCount ?? 'N/A'}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(supplier.totalPoValue)}
                          </TableCell>
                          <TableCell className="text-center">
                            {supplier.deliveryRating !== null && supplier.deliveryRating !== undefined 
                              ? supplier.deliveryRating.toFixed(2)
                              : 'N/A'}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={rating.className}>
                              {rating.label}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-6">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SupplierPerformanceReportPage;
